import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { authUtil } from 'src/utils';

/**
 * Data layer for author-hosted Critique Groups (`/api/v2/critique-groups`).
 *
 * The whole router is behind the `critique_groups` feature flag server-side and
 * fails CLOSED with 403 `FEATURE_DISABLED` — including on reads. Callers should
 * gate on the flag first (see `useCritiqueGroupsEnabled`) so a dark deployment
 * never fires these at all.
 */

// ── Types ───────────────────────────────────────────────────────────────────

export type CritiqueFeedbackVisibility = 'host_only' | 'members_shared';
export type CritiqueRevealPolicy = 'immediate' | 'after_own';
export type CritiqueGroupVisibility = 'invite_only' | 'request' | 'listed';
export type CritiqueMemberRole = 'member' | 'admin' | 'moderator' | 'blocked';
/** Roles the API will accept in a role change (`admin` is not assignable). */
export type AssignableRole = 'member' | 'moderator' | 'blocked';
export type DeliverableType = 'reached_part' | 'comment' | 'survey' | 'custom';

export type CritiqueUserRef = {
  _id: string;
  displayName?: string;
};

export type CritiqueMember = {
  _id: string;
  group: string;
  role: CritiqueMemberRole;
  // Populated to {_id, displayName} by GET /:groupId; a bare uid elsewhere.
  user: CritiqueUserRef | string;
};

/** One resolution of a book's cover art (Cover.highRes / Cover.thumbnail). */
export type CritiqueCoverImage = {
  url?: string;
  width?: number;
  height?: number;
};

/** A book's cover, as populated on a linked-book ref (bare id when unpopulated). */
export type CritiqueCover = {
  _id?: string;
  highRes?: CritiqueCoverImage;
  thumbnail?: CritiqueCoverImage;
};

export type CritiqueBookRef = {
  _id: string;
  title?: string;
  author?: string;
  // Populated on GET list/single; absent (or a bare id) otherwise.
  cover?: CritiqueCover | string;
};

/** The group's own uploaded cover graphic (Image doc), populated on list/single. */
export type CritiqueImageRef = {
  _id: string;
  url?: string;
  width?: number;
  height?: number;
};

export type CritiqueDeliverable = {
  type: DeliverableType;
  description?: string;
  part?: string;
  survey?: string;
  /** Default true. `false` = guiding only: never reminds, never blocks "done". */
  required?: boolean;
};

export type CritiqueCheckpoint = {
  _id: string;
  label: string;
  dueAt: string;
  part?: string;
  deliverable?: CritiqueDeliverable;
  createdAt?: string;
  updatedAt?: string;
};

export type CritiqueGroup = {
  _id: string;
  id?: string;
  name: string;
  createdBy: string;
  type: 'community' | 'critique';
  profileColor?: string;
  // The host's uploaded group cover graphic; a bare id or absent when unset.
  coverImage?: CritiqueImageRef | string;
  books: Array<CritiqueBookRef | string>;
  visibility: CritiqueGroupVisibility;
  memberCap?: number;
  feedbackVisibility: CritiqueFeedbackVisibility;
  revealPolicy: CritiqueRevealPolicy;
  checkpoints: CritiqueCheckpoint[];
  // Present only when populated (GET list/single).
  members?: CritiqueMember[];
  memberCount?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type CritiqueJoinRequest = {
  _id: string;
  group: string;
  user: CritiqueUserRef | string;
  createdAt?: string;
};

/** Where the viewer stands relative to a previewed group. */
export type CritiqueRequestStatus = 'none' | 'pending' | 'member' | 'declined';

/**
 * The PUBLIC projection of a group — what a non-member is allowed to see
 * (`GET /:groupId/preview` and `GET /discover`). Deliberately NOT a subset of
 * `CritiqueGroup`: it is a different, flattened shape produced by the server's
 * allowlist cleaner, and keeping the types separate is what stops a component
 * from reaching for `group.members` on something that will never have it.
 */
export type CritiquePublicGroup = {
  _id: string;
  name: string | null;
  visibility: CritiqueGroupVisibility;
  coverUrl: string | null;
  book: { title: string; coverUrl: string | null } | null;
  host: { _id?: string; displayName: string | null } | null;
  memberCount: number;
  memberCap: number | null;
  isFull: boolean;
  createdAt?: string;
  /** Preview only — the catalog omits it. */
  myRequestStatus?: CritiqueRequestStatus;
};

export type CritiqueDiscoverPage = {
  groups: CritiquePublicGroup[];
  hasMore: boolean;
};

export type CritiqueComment = {
  aiOrigin?: string;
  _id: string;
  user?: CritiqueUserRef;
  book?: string;
  part?: string;
  text?: string;
  createdAt?: string;
};

export type CritiqueSurveyAnswer = {
  aiOrigin?: string;
  _id: string;
  survey?: string;
  surveyQuestion?: string;
  user?: CritiqueUserRef;
  answer?: unknown;
  createdAt?: string;
};

/**
 * The member-facing feedback view. The server is the source of truth: items the
 * requester may not see are FILTERED OUT server-side, never flagged — so this
 * payload contains only revealed feedback and there is nothing to reconstruct.
 *
 * `unlockedParts` is present ONLY when the after_own gate applies. It is the
 * requester's OWN contributed parts, so it says what THEY unlocked — never
 * anything about other members or whether hidden feedback even exists. Its
 * absence means no gate (host view, `immediate`, or `host_only`).
 */
export type CritiqueFeedbackView = {
  feedbackVisibility: CritiqueFeedbackVisibility;
  revealPolicy: CritiqueRevealPolicy;
  unlockedParts?: string[];
  comments: CritiqueComment[];
  surveyAnswers: CritiqueSurveyAnswer[];
};

export type CritiqueCompletionRow = {
  user: CritiqueUserRef;
  met: boolean;
  /** `blocksDone ? met : true` — a guiding checkpoint is always "done". */
  done: boolean;
};

export type CritiqueCompletion = {
  checkpoint: {
    _id: string;
    label: string;
    dueAt: string;
    deliverable?: CritiqueDeliverable;
    blocksDone: boolean;
  };
  completion: CritiqueCompletionRow[];
};

export type CritiquePost = {
  _id: string;
  id?: string;
  createdBy: CritiqueUserRef;
  text: string;
  /** Optional topic/subject line (topic threads). Absent on legacy posts. */
  title?: string;
  group: string;
  pinned: boolean;
  /** Present only on GET /posts. */
  replyCount?: number;
  createdAt?: string;
  /** Newest reply time (or creation time). Drives latest-activity ordering. */
  lastActivityAt?: string;
};

export type CritiquePostReply = {
  _id: string;
  createdBy: CritiqueUserRef;
  post: string;
  text: string;
  createdAt?: string;
};

/** Error codes the API returns in `error` alongside `err` **or** `msg`. */
export type CritiqueErrorCode =
  | 'FEATURE_DISABLED'
  | 'UPGRADE_REQUIRED_MAX_GROUPS'
  | 'UPGRADE_REQUIRED_MAX_BOOKS'
  | 'UPGRADE_REQUIRED_PUBLIC_GROUP'
  | 'INVALID_VISIBILITY'
  | 'MEMBER_CAP_REACHED'
  | 'BOOK_NOT_OWNED'
  | 'INVITE_ONLY'
  | 'NOT_SELF_MARKABLE'
  | string;

/**
 * The router uses BOTH `{err}` and `{msg}` for the human message, and many
 * 403/404s carry no `error` code at all. 500s send a raw Error, which
 * serializes to `{}` — hence the status fallback. Normalizing here keeps every
 * call site from re-deriving it.
 */
export class CritiqueApiError extends Error {
  code?: CritiqueErrorCode;
  status: number;

  constructor(message: string, status: number, code?: CritiqueErrorCode) {
    super(message);
    this.name = 'CritiqueApiError';
    this.status = status;
    this.code = code;
  }
}

// ── Fetch plumbing ──────────────────────────────────────────────────────────

const apiBase = () =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}`;

const critiqueFetch = async <T>(
  path: string,
  init: RequestInit = {}
): Promise<T> => {
  const response = await fetch(`${apiBase()}/critique-groups${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'br-token': await authUtil.getFreshIdToken(),
      ...(init.headers || {}),
    },
  });
  if (!response.ok) {
    // A 500 body is `{}` and some errors aren't JSON at all — never let parsing
    // failure mask the status.
    const body = await response.json().catch(() => ({}) as any);
    throw new CritiqueApiError(
      body.err || body.msg || response.statusText || 'Request failed',
      response.status,
      body.error
    );
  }
  if (response.status === 204) return undefined as unknown as T;
  return response.json();
};

/**
 * Token-LESS fetch for the one unauthenticated endpoint (`/public-preview`),
 * used by the logged-out landing page. Sends NO `br-token` — `critiqueFetch`
 * always attaches one via `authUtil.getFreshIdToken()`, which would throw for a
 * signed-out visitor, so the landing needs its own plumbing. Same error shape.
 */
const publicCritiqueFetch = async <T>(path: string): Promise<T> => {
  const response = await fetch(`${apiBase()}/critique-groups${path}`);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}) as any);
    throw new CritiqueApiError(
      body.err || body.msg || response.statusText || 'Request failed',
      response.status,
      body.error
    );
  }
  return response.json();
};

// ── Query keys ──────────────────────────────────────────────────────────────

export const critiqueKeys = {
  all: ['critiqueGroups'] as const,
  list: () => [...critiqueKeys.all, 'list'] as const,
  group: (groupId: string) => [...critiqueKeys.all, 'group', groupId] as const,
  requests: (groupId: string) =>
    [...critiqueKeys.all, 'requests', groupId] as const,
  posts: (groupId: string) => [...critiqueKeys.all, 'posts', groupId] as const,
  replies: (groupId: string, postId: string) =>
    [...critiqueKeys.all, 'replies', groupId, postId] as const,
  feedback: (groupId: string, bookId: string) =>
    [...critiqueKeys.all, 'feedback', groupId, bookId] as const,
  completion: (groupId: string, checkpointId: string) =>
    [...critiqueKeys.all, 'completion', groupId, checkpointId] as const,
  preview: (groupId: string) =>
    [...critiqueKeys.all, 'preview', groupId] as const,
  publicPreview: (groupId: string) =>
    [...critiqueKeys.all, 'publicPreview', groupId] as const,
  discover: (search: string) =>
    [...critiqueKeys.all, 'discover', search] as const,
};

const queryDefaults = {
  refetchOnWindowFocus: false,
  staleTime: 1000 * 30,
  retry: 1,
};

// ── Reads ───────────────────────────────────────────────────────────────────

export const useCritiqueGroupList = (options?: { enabled?: boolean }) =>
  useQuery<CritiqueGroup[], CritiqueApiError>({
    queryKey: critiqueKeys.list(),
    queryFn: () => critiqueFetch<CritiqueGroup[]>(''),
    ...queryDefaults,
    enabled: options?.enabled !== false,
  });

export const useCritiqueGroup = (
  groupId?: string,
  options?: { enabled?: boolean }
) =>
  useQuery<CritiqueGroup, CritiqueApiError>({
    queryKey: critiqueKeys.group(groupId || ''),
    queryFn: () => critiqueFetch<CritiqueGroup>(`/${groupId}`),
    ...queryDefaults,
    enabled: !!groupId && options?.enabled !== false,
  });

/**
 * Open join requests. Host/moderator only — a plain member gets 403, so callers
 * must only mount this for a host/moderator. `retry: false` keeps an expected
 * 403 from being retried.
 */
export const useCritiqueJoinRequests = (
  groupId?: string,
  options?: { enabled?: boolean }
) =>
  useQuery<CritiqueJoinRequest[], CritiqueApiError>({
    queryKey: critiqueKeys.requests(groupId || ''),
    queryFn: () => critiqueFetch<CritiqueJoinRequest[]>(`/${groupId}/requests`),
    ...queryDefaults,
    retry: false,
    enabled: !!groupId && options?.enabled !== false,
  });

export const useCritiqueFeedback = (
  groupId?: string,
  bookId?: string,
  options?: { enabled?: boolean }
) =>
  useQuery<CritiqueFeedbackView, CritiqueApiError>({
    queryKey: critiqueKeys.feedback(groupId || '', bookId || ''),

    queryFn: () =>
      critiqueFetch<CritiqueFeedbackView>(
        `/${groupId}/books/${bookId}/feedback`
      ),
    ...queryDefaults,
    enabled: !!groupId && !!bookId && options?.enabled !== false,
  });

export const useCritiqueCheckpointCompletion = (
  groupId?: string,
  checkpointId?: string,
  options?: { enabled?: boolean }
) =>
  useQuery<CritiqueCompletion, CritiqueApiError>({
    queryKey: critiqueKeys.completion(groupId || '', checkpointId || ''),

    queryFn: () =>
      critiqueFetch<CritiqueCompletion>(
        `/${groupId}/checkpoints/${checkpointId}/completion`
      ),
    ...queryDefaults,
    retry: false,
    enabled: !!groupId && !!checkpointId && options?.enabled !== false,
  });

export const useCritiquePosts = (
  groupId?: string,
  options?: { enabled?: boolean; limit?: number }
) =>
  useQuery<CritiquePost[], CritiqueApiError>({
    queryKey: critiqueKeys.posts(groupId || ''),

    queryFn: () =>
      critiqueFetch<CritiquePost[]>(
        `/${groupId}/posts?limit=${options?.limit ?? 20}`
      ),
    ...queryDefaults,
    enabled: !!groupId && options?.enabled !== false,
  });

export const useCritiquePostReplies = (
  groupId?: string,
  postId?: string,
  options?: UseQueryOptions<CritiquePostReply[], CritiqueApiError>
) =>
  useQuery<CritiquePostReply[], CritiqueApiError>({
    queryKey: critiqueKeys.replies(groupId || '', postId || ''),
    queryFn: () =>
      critiqueFetch<CritiquePostReply[]>(`/${groupId}/posts/${postId}/replies`),
    ...queryDefaults,
    enabled: !!groupId && !!postId,
    ...(options || {}),
  });

// ── Writes ──────────────────────────────────────────────────────────────────

export type CreateCritiqueGroupInput = {
  name: string;
  books?: string[];
  feedbackVisibility?: CritiqueFeedbackVisibility;
  revealPolicy?: CritiqueRevealPolicy;
  visibility?: CritiqueGroupVisibility;
  memberCap?: number;
  checkpoints?: Array<Partial<CritiqueCheckpoint>>;
};

export const useCreateCritiqueGroup = () => {
  const queryClient = useQueryClient();
  return useMutation<CritiqueGroup, CritiqueApiError, CreateCritiqueGroupInput>(
    {
      mutationFn: input =>
        critiqueFetch<CritiqueGroup>('', {
          method: 'POST',
          body: JSON.stringify(input),
        }),

      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
      },
    }
  );
};

/**
 * Delete a critique group the caller hosts (frees their tier slot). Host-only
 * server-side (authorized by ownership, so it works even for an orphaned group
 * whose membership is missing). Invalidates the list so the card disappears.
 */
export const useDeleteCritiqueGroup = () => {
  const queryClient = useQueryClient();
  return useMutation<{ msg: string }, CritiqueApiError, { groupId: string }>({
    mutationFn: ({ groupId }) =>
      critiqueFetch<{ msg: string }>(`/${groupId}`, { method: 'DELETE' }),

    onSuccess: (_data, { groupId }) => {
      // The group is gone — drop its detail cache so a still-mounted detail
      // view (or a back-nav) can't refetch it into a 404, then refresh the list.
      queryClient.removeQueries({ queryKey: critiqueKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
    },
  });
};

export const useUpdateFeedbackSettings = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    {
      feedbackVisibility: CritiqueFeedbackVisibility;
      revealPolicy: CritiqueRevealPolicy;
    },
    CritiqueApiError,
    {
      feedbackVisibility?: CritiqueFeedbackVisibility;
      revealPolicy?: CritiqueRevealPolicy;
    }
  >({
    mutationFn: input =>
      critiqueFetch(`/${groupId}/feedback-settings`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),

    onSuccess: () => {
      // The response is only the two changed fields, not the group — refetch
      // rather than patching the cached group from a partial payload.
      queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
    },
  });
};

/**
 * Set who can find and join a group: private (`invite_only`) / link-only
 * (`request`) / public (`listed`). Host-only and PAID-gated server-side — a
 * free host gets `UPGRADE_REQUIRED_PUBLIC_GROUP`, which the caller turns into
 * the upgrade prompt. Going private is always allowed.
 */
export const useUpdateGroupVisibility = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    CritiqueGroup,
    CritiqueApiError,
    { visibility: CritiqueGroupVisibility }
  >({
    mutationFn: input =>
      critiqueFetch<CritiqueGroup>(`/${groupId}/visibility`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
    },
  });
};

/**
 * The public metadata preview of a `request`/`listed` group, for a non-member.
 * `retry: false` — a private group answers 404 and a blocked user 403, neither
 * of which is worth retrying.
 */
export const useCritiqueGroupPreview = (
  groupId?: string,
  options?: { enabled?: boolean }
) =>
  useQuery<CritiquePublicGroup, CritiqueApiError>({
    queryKey: critiqueKeys.preview(groupId || ''),
    queryFn: () => critiqueFetch<CritiquePublicGroup>(`/${groupId}/preview`),
    ...queryDefaults,
    retry: false,
    enabled: !!groupId && options?.enabled !== false,
  });

/**
 * The UNAUTHENTICATED public preview, for the logged-out landing page. Hits the
 * app-level `/public-preview` endpoint with no token. `invite_only` groups 404
 * and a dark feature 403s — neither is worth retrying.
 */
export const useCritiqueGroupPublicPreview = (
  groupId?: string,
  options?: { enabled?: boolean }
) =>
  useQuery<CritiquePublicGroup, CritiqueApiError>({
    queryKey: critiqueKeys.publicPreview(groupId || ''),

    queryFn: () =>
      publicCritiqueFetch<CritiquePublicGroup>(`/${groupId}/public-preview`),
    ...queryDefaults,
    retry: false,
    enabled: !!groupId && options?.enabled !== false,
  });

/** The public discovery catalog of `listed` groups. */
// The discovery feed's page size. MUST match the API's DEFAULT_LIMIT in
// routes/critique-groups/discover.js: the next page's `skip` advances by the
// RAW page size, not by the number of groups we got back — the server filters
// lapsed-host groups out AFTER taking `limit` rows, so a page can return fewer
// than PAGE_SIZE cleaned groups while there are still more raw rows to page.
const DISCOVER_PAGE_SIZE = 20;

/**
 * The public discovery catalog of `listed` groups, paginated. `useInfiniteQuery`
 * accumulates pages; `getNextPageParam` reads the server's `hasMore` and derives
 * the next `skip` from the page COUNT × page size (see the note above).
 */
export const useCritiqueDiscover = (
  search = '',
  options?: { enabled?: boolean }
) =>
  useInfiniteQuery<CritiqueDiscoverPage, CritiqueApiError>({
    queryKey: critiqueKeys.discover(search),
    queryFn: ({ pageParam }) =>
      critiqueFetch<CritiqueDiscoverPage>(
        `/discover?skip=${pageParam}${
          search ? `&search=${encodeURIComponent(search)}` : ''
        }`
      ),
    initialPageParam: 0,
    ...queryDefaults,
    enabled: options?.enabled !== false,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length * DISCOVER_PAGE_SIZE : undefined,
  });

/** Ask to join a `request`/`listed` group. */
export const useRequestToJoin = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<CritiqueJoinRequest, CritiqueApiError, void>({
    mutationFn: () =>
      critiqueFetch<CritiqueJoinRequest>(`/${groupId}/requests`, {
        method: 'POST',
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: critiqueKeys.preview(groupId),
      });
    },
  });
};

/** Withdraw your own pending request. */
export const useWithdrawJoinRequest = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<{ msg: string }, CritiqueApiError, void>({
    mutationFn: () =>
      critiqueFetch(`/${groupId}/requests/mine`, {
        method: 'DELETE',
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: critiqueKeys.preview(groupId),
      });
    },
  });
};

export const useLinkBook = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<CritiqueGroup, CritiqueApiError, { bookId: string }>({
    mutationFn: input =>
      critiqueFetch<CritiqueGroup>(`/${groupId}/books`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
    },
  });
};

export const useUnlinkBook = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<CritiqueGroup, CritiqueApiError, { bookId: string }>({
    mutationFn: ({ bookId }) =>
      critiqueFetch<CritiqueGroup>(`/${groupId}/books/${bookId}`, {
        method: 'DELETE',
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
    },
  });
};

/**
 * Write the new `coverImage` straight into the cached group + list so every
 * screen reading them (the detail header, the group cards, the settings preview)
 * repaints IN PLACE the moment the mutation resolves — instead of flashing the
 * old/empty cover until an invalidation round-trip lands. Only `coverImage` is
 * touched; `members`/`books` on the cached group are left intact (the cover
 * endpoints don't return them). Pass `undefined` to clear the cover on remove.
 */
const patchGroupCover = (
  queryClient: ReturnType<typeof useQueryClient>,
  groupId: string,
  coverImage: CritiqueImageRef | undefined
) => {
  queryClient.setQueryData<CritiqueGroup | undefined>(
    critiqueKeys.group(groupId),
    old => (old ? { ...old, coverImage } : old)
  );
  queryClient.setQueryData<CritiqueGroup[] | undefined>(
    critiqueKeys.list(),
    old =>
      old ? old.map(g => (g._id === groupId ? { ...g, coverImage } : g)) : old
  );
};

/**
 * Upload the group's cover graphic (host only). This is a multipart POST, so it
 * bypasses `critiqueFetch` (which forces `Content-Type: application/json`) and
 * sends `FormData` directly — the browser sets the multipart boundary. The API
 * route lives outside the flag-gated router but re-checks the flag + host, so a
 * non-host or a dark deployment gets a clean 403.
 */
export const useUploadGroupCover = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<CritiqueGroup, CritiqueApiError, { file: File }>({
    mutationFn: async ({ file }) => {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch(
        `${apiBase()}/critique-groups/${groupId}/cover`,
        {
          method: 'POST',
          headers: { 'br-token': await authUtil.getFreshIdToken() },
          body: form,
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}) as any);
        throw new CritiqueApiError(
          // `message` is multer's shape (app.ts handleMulterError, e.g.
          // FileTooLarge); without it an oversized file reads as a bare "Bad
          // Request" instead of saying what the limit is.
          body.err ||
            body.msg ||
            body.message ||
            response.statusText ||
            'Upload failed',
          response.status,
          body.error
        );
      }
      return response.json();
    },

    onSuccess: data => {
      // Paint the new cover immediately from the authoritative response, then
      // invalidate to reconcile the rest of the group (members/books).
      const cover = data?.coverImage;
      if (cover && typeof cover !== 'string') {
        patchGroupCover(queryClient, groupId, cover);
      }
      queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
    },
  });
};

/** Remove the group's cover graphic (host only), reverting to the fallback. */
export const useRemoveGroupCover = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<CritiqueGroup, CritiqueApiError, void>({
    mutationFn: () =>
      critiqueFetch<CritiqueGroup>(`/${groupId}/cover`, { method: 'DELETE' }),

    onSuccess: () => {
      // Clear the cover in place so the preview/header fall back at once.
      patchGroupCover(queryClient, groupId, undefined);
      queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
    },
  });
};

/** Invalidate everything a membership change can affect (roster + seat count). */
const invalidateMembership = (queryClient: any, groupId: string) => {
  queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
  queryClient.invalidateQueries({ queryKey: critiqueKeys.requests(groupId) });
  queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
};

export const useUpdateMemberRole = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    CritiqueMember,
    CritiqueApiError,
    { userId: string; role: AssignableRole }
  >({
    mutationFn: ({ userId, role }) =>
      critiqueFetch<CritiqueMember>(`/${groupId}/members/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ role }),
      }),

    onSuccess: () => invalidateMembership(queryClient, groupId),
  });
};

export const useRemoveMember = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<{ msg: string }, CritiqueApiError, { userId: string }>({
    mutationFn: ({ userId }) =>
      critiqueFetch<{ msg: string }>(`/${groupId}/members/${userId}`, {
        method: 'DELETE',
      }),

    onSuccess: () => invalidateMembership(queryClient, groupId),
  });
};

export const useResolveJoinRequest = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    CritiqueApiError,
    { requestId: string; action: 'approve' | 'decline' }
  >({
    mutationFn: ({ requestId, action }) =>
      critiqueFetch(`/${groupId}/requests/${requestId}`, {
        method: 'PUT',
        body: JSON.stringify({ action }),
      }),

    onSuccess: () => invalidateMembership(queryClient, groupId),
  });
};

export type CheckpointInput = {
  label: string;
  dueAt: string;
  part?: string | null;
  deliverable?: CritiqueDeliverable | null;
};

export const useAddCheckpoint = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<CritiqueCheckpoint, CritiqueApiError, CheckpointInput>({
    mutationFn: input =>
      critiqueFetch<CritiqueCheckpoint>(`/${groupId}/checkpoints`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
    },
  });
};

export const useUpdateCheckpoint = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    CritiqueCheckpoint,
    CritiqueApiError,
    { checkpointId: string } & Partial<CheckpointInput>
  >({
    mutationFn: ({ checkpointId, ...input }) =>
      critiqueFetch<CritiqueCheckpoint>(
        `/${groupId}/checkpoints/${checkpointId}`,
        {
          method: 'PUT',
          body: JSON.stringify(input),
        }
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
    },
  });
};

export const useRemoveCheckpoint = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<unknown, CritiqueApiError, { checkpointId: string }>({
    mutationFn: ({ checkpointId }) =>
      critiqueFetch(`/${groupId}/checkpoints/${checkpointId}`, {
        method: 'DELETE',
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: critiqueKeys.list() });
    },
  });
};

/**
 * Self-mark a CUSTOM deliverable done. The API rejects any other deliverable
 * type with 400 `NOT_SELF_MARKABLE` — only offer this for `type: 'custom'`.
 * `userId` (host/moderator marking someone else) is optional.
 */
export const useCompleteCheckpoint = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    { checkpoint: string; user: string; completed: boolean },
    CritiqueApiError,
    { checkpointId: string; userId?: string }
  >({
    mutationFn: ({ checkpointId, userId }) =>
      critiqueFetch(`/${groupId}/checkpoints/${checkpointId}/complete`, {
        method: 'POST',
        body: JSON.stringify(userId ? { userId } : {}),
      }),

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: critiqueKeys.group(groupId) });
      queryClient.invalidateQueries({
        queryKey: critiqueKeys.completion(groupId, variables.checkpointId),
      });
    },
  });
};

export const useCreatePost = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    CritiquePost,
    CritiqueApiError,
    { text: string; title?: string; pinned?: boolean }
  >({
    mutationFn: input =>
      critiqueFetch<CritiquePost>(`/${groupId}/posts`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: critiqueKeys.posts(groupId) });
    },
  });
};

export const usePinPost = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    CritiquePost,
    CritiqueApiError,
    { postId: string; pinned: boolean }
  >({
    mutationFn: ({ postId, pinned }) =>
      critiqueFetch<CritiquePost>(`/${groupId}/posts/${postId}/pin`, {
        method: 'PUT',
        body: JSON.stringify({ pinned }),
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: critiqueKeys.posts(groupId) });
    },
  });
};

export const useCreateReply = (groupId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    CritiquePostReply,
    CritiqueApiError,
    { postId: string; text: string }
  >({
    mutationFn: ({ postId, text }) =>
      critiqueFetch<CritiquePostReply>(`/${groupId}/posts/${postId}/replies`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      }),

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: critiqueKeys.replies(groupId, variables.postId),
      });
      // replyCount lives on the post list, so it's stale now too.
      queryClient.invalidateQueries({ queryKey: critiqueKeys.posts(groupId) });
    },
  });
};
